import type { Gender, ReportReason, SearchPrefs } from '@repo/shared/domain';
import type { BillingInfo, PlanId } from '@repo/shared/plans';
import type {
  ClientGeo,
  ClientMessage,
  PeerInfo,
  ServerErrorCode,
  ServerMessage,
} from '@repo/shared/protocol';
import { MAX_CHAT_LENGTH, PeerLink, type PeerQuality, type PeerState } from './peer.ts';
import { FATAL_CLOSE_CODES, SignalingClient, type SignalingStatus } from './signaling.ts';

export type Phase = 'idle' | 'searching' | 'connecting' | 'connected';

export interface ChatLine {
  id: number;
  from: 'me' | 'peer';
  text: string;
}

export interface SessionError {
  code: ServerErrorCode | 'connection_lost';
  message: string;
}

export interface SearchRequest {
  gender: Gender;
  prefs: SearchPrefs;
  geo: ClientGeo | null;
}

export interface SessionSnapshot {
  signaling: SignalingStatus;
  phase: Phase;
  matchId: string | null;
  peer: PeerInfo | null;
  remoteStream: MediaStream | null;
  chat: ChatLine[];
  chatOpen: boolean;
  online: number;
  plan: PlanId;
  planExpiresAt: number | null;
  devPlans: boolean;
  /** Zahlungsabwicklung laut Server – null, solange Stripe nicht eingerichtet ist. */
  billing: BillingInfo | null;
  detectedCountry: string | null;
  searchingSince: number | null;
  /** Seit wann das Gespräch läuft, für die Dauer neben dem Bild */
  connectedSince: number | null;
  /** Zustand der Leitung, null solange nichts gemessen wurde */
  quality: PeerQuality | null;
  /** Zählt bei jedem Gerätewechsel hoch, damit das eigene Bild neu gesetzt wird */
  localRevision: number;
  /** Kurzer Hinweis, z. B. "Dein Gegenüber hat weitergeklickt" (id ändert sich bei jedem Hinweis). */
  notice: { id: number; text: string } | null;
  error: SessionError | null;
  micEnabled: boolean;
  camEnabled: boolean;
}

const NO_MATCH = {
  matchId: null,
  peer: null,
  remoteStream: null,
  chat: [],
  chatOpen: false,
  connectedSince: null,
  quality: null,
} satisfies Partial<SessionSnapshot>;

/**
 * Zustand eines Chat-Tabs. Verbindet Signaling (Server) und PeerLink (WebRTC) zu einem Ablauf
 * idle → searching → connecting → connected. React liest nur Snapshots und ruft Aktionen auf.
 */
export class ChatSession {
  static #nextId = 1;
  /** Eindeutig pro Instanz – dient React als key. */
  readonly id = ChatSession.#nextId++;
  readonly #listeners = new Set<() => void>();
  readonly #signaling: SignalingClient;
  readonly #localStream: MediaStream;
  #snapshot: SessionSnapshot;
  #peer: PeerLink | null = null;
  #entitlementToken: string | null;
  /** Ist die Verbindung bereit (welcome empfangen, Freischaltung gesendet)? */
  #ready = false;
  /** Möchte die Person gerade chatten? Bleibt über Verbindungsabbrüche hinweg erhalten. */
  #active = false;
  #search: SearchRequest | null = null;
  #sentSearch: SearchRequest | null = null;
  #counter = 0;

  constructor(options: {
    localStream: MediaStream;
    entitlementToken: string | null;
    signalingUrl?: string;
  }) {
    this.#localStream = options.localStream;
    this.#entitlementToken = options.entitlementToken;
    this.#snapshot = {
      signaling: 'connecting',
      phase: 'idle',
      ...NO_MATCH,
      online: 0,
      plan: 'free',
      planExpiresAt: null,
      devPlans: false,
      billing: null,
      detectedCountry: null,
      searchingSince: null,
      localRevision: 0,
      notice: null,
      error: null,
      micEnabled: allEnabled(options.localStream.getAudioTracks()),
      camEnabled: allEnabled(options.localStream.getVideoTracks()),
    };
    this.#signaling = new SignalingClient({
      url: options.signalingUrl,
      onMessage: (msg) => this.#onServerMessage(msg),
      onStatus: (status, closeCode) => this.#onSignalingStatus(status, closeCode),
    });
    this.#signaling.connect();
  }

  // ── React ────────────────────────────────────────────────────────────────

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  getSnapshot = (): SessionSnapshot => this.#snapshot;

  // ── Aktionen ─────────────────────────────────────────────────────────────

  /** Suche starten – auch nach "Stopp" oder einem Fehler. */
  start(request: SearchRequest): void {
    this.#search = request;
    this.#active = true;
    const { phase, searchingSince } = this.#snapshot;
    if (phase === 'connecting' || phase === 'connected') return;
    this.#update({ phase: 'searching', searchingSince: searchingSince ?? Date.now(), error: null });
    this.#sendFind();
  }

  /**
   * Filter ändern: bei laufender Suche sofort (der Warteplatz bleibt erhalten), im Chat ab dem
   * nächsten Match, im Leerlauf nur merken.
   */
  updateSearch(request: SearchRequest): void {
    this.#search = request;
    if (this.#active && this.#snapshot.phase === 'searching' && this.#searchChanged()) {
      this.#sendFind();
    }
  }

  next(): void {
    const { phase, matchId } = this.#snapshot;
    if (!this.#search || phase === 'searching') return;
    if (phase === 'idle') return this.start(this.#search);

    this.#closePeer();
    this.#update({ phase: 'searching', ...NO_MATCH, searchingSince: Date.now() });
    if (this.#searchChanged()) this.#sendFind();
    else this.#send({ type: 'next', matchId });
  }

  stop(): void {
    this.#active = false;
    this.#closePeer();
    this.#send({ type: 'stop' });
    this.#update({ phase: 'idle', ...NO_MATCH, searchingSince: null });
  }

  /** Meldet genau das Match, das beim Öffnen des Dialogs lief – nie versehentlich das nächste. */
  report(reason: ReportReason, matchId: string): void {
    if (matchId !== this.#snapshot.matchId) return;
    this.#send({ type: 'report', matchId, reason });
    this.#closePeer();
    this.#notice('Danke fürs Melden. Wir suchen dir jemand Neues.');
    this.#update({ phase: 'searching', ...NO_MATCH, searchingSince: Date.now() });
    // Der Server sucht nach einer Meldung selbst weiter; nur geänderte Filter nachreichen.
    if (this.#searchChanged()) this.#sendFind();
  }

  sendChat(text: string): void {
    const trimmed = text.trim().slice(0, MAX_CHAT_LENGTH);
    if (trimmed && this.#peer?.sendChat(trimmed)) this.#appendChat('me', trimmed);
  }

  setEntitlement(token: string | null): void {
    this.#entitlementToken = token;
    this.#send({ type: 'entitlement', token });
  }

  setMicEnabled(enabled: boolean): void {
    for (const track of this.#localStream.getAudioTracks()) track.enabled = enabled;
    this.#update({ micEnabled: enabled });
  }

  setCamEnabled(enabled: boolean): void {
    for (const track of this.#localStream.getVideoTracks()) track.enabled = enabled;
    this.#update({ camEnabled: enabled });
  }

  /**
   * Kamera oder Mikrofon wechseln. Der Stream bleibt dabei derselbe, damit weder die Sitzung
   * noch ein laufendes Gespräch neu aufgebaut werden muss.
   */
  setLocalTrack(track: MediaStreamTrack): void {
    const isVideo = track.kind === 'video';
    track.enabled = isVideo ? this.#snapshot.camEnabled : this.#snapshot.micEnabled;
    for (const existing of this.#localStream.getTracks()) {
      if (existing.kind !== track.kind) continue;
      this.#localStream.removeTrack(existing);
      existing.stop();
    }
    this.#localStream.addTrack(track);
    void this.#peer?.replaceTrack(track);
    this.#update({ localRevision: this.#snapshot.localRevision + 1 });
  }

  dismissError(): void {
    this.#update({ error: null });
  }

  destroy(): void {
    this.#active = false;
    this.#closePeer();
    this.#signaling.close();
    this.#listeners.clear();
  }

  // ── Server ───────────────────────────────────────────────────────────────

  #onServerMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome':
        this.#ready = true;
        this.#update({
          online: msg.online,
          plan: msg.plan,
          planExpiresAt: null,
          devPlans: msg.devPlans,
          billing: msg.billing,
          detectedCountry: msg.country,
        });
        // Reihenfolge zählt: erst Freischaltung, dann Suche – der Server verarbeitet in Reihenfolge.
        if (this.#entitlementToken)
          this.#send({ type: 'entitlement', token: this.#entitlementToken });
        if (this.#active) {
          this.#closePeer();
          this.#update({ phase: 'searching', ...NO_MATCH });
          this.#sendFind();
        }
        return;

      case 'searching':
        this.#update({ online: msg.online });
        return;

      case 'matched': {
        if (!this.#active) return; // "Stopp" ist schon unterwegs – der Server beendet das Match
        this.#closePeer();
        const { matchId } = msg;
        const isCurrent = () => this.#snapshot.matchId === matchId;
        this.#update({ phase: 'connecting', ...NO_MATCH, matchId, peer: msg.peer, error: null });
        this.#peer = new PeerLink({
          initiator: msg.initiator,
          iceServers: msg.iceServers,
          iceTransportPolicy: msg.iceTransportPolicy,
          localStream: this.#localStream,
          sendSignal: (data) => this.#send({ type: 'signal', matchId, data }),
          onRemoteStream: (stream) => {
            if (isCurrent()) this.#update({ remoteStream: stream });
          },
          onState: (state) => {
            if (isCurrent()) this.#onPeerState(state);
          },
          onChatOpen: () => {
            if (isCurrent()) this.#update({ chatOpen: true });
          },
          onChatMessage: (text) => {
            if (isCurrent()) this.#appendChat('peer', text);
          },
          onQuality: (quality) => {
            if (isCurrent()) this.#update({ quality });
          },
        });
        return;
      }

      case 'signal':
        if (msg.matchId === this.#snapshot.matchId) this.#peer?.handleSignal(msg.data);
        return;

      case 'peer_left':
        if (msg.matchId !== this.#snapshot.matchId) return;
        this.#closePeer();
        this.#notice(
          msg.reason === 'disconnected'
            ? 'Die Verbindung zu deinem Gegenüber ist abgebrochen.'
            : 'Dein Gegenüber hat weitergeklickt.',
        );
        this.#update({ phase: 'searching', ...NO_MATCH, searchingSince: Date.now() });
        if (!this.#active) return;
        if (this.#searchChanged()) this.#sendFind();
        else this.#send({ type: 'next', matchId: null });
        return;

      case 'plan':
        this.#update({ plan: msg.plan, planExpiresAt: msg.expiresAt });
        return;

      case 'online':
        this.#update({ online: msg.count });
        return;

      case 'error':
        this.#onServerError(msg.code, msg.message);
        return;

      case 'idle':
        return;
    }
  }

  #onServerError(code: ServerErrorCode, message: string): void {
    if (code === 'rate_limited') return this.#notice(message);
    if (code === 'invalid_message') {
      console.warn('Server hat eine Nachricht abgelehnt:', message);
      return;
    }
    // plan_required, location_required, country_unknown, banned, too_many_connections
    this.#active = false;
    this.#closePeer();
    this.#update({ phase: 'idle', ...NO_MATCH, searchingSince: null, error: { code, message } });
  }

  #onSignalingStatus(status: SignalingStatus, closeCode?: number): void {
    if (status !== 'open') this.#ready = false;
    if (status === 'closed' && this.#snapshot.phase !== 'idle') {
      // Der Server beendet laufende Chats bei Verbindungsverlust – nach dem Wiederverbinden neu suchen.
      this.#closePeer();
      this.#update({ phase: this.#active ? 'searching' : 'idle', ...NO_MATCH });
    }
    if (status === 'closed' && closeCode !== undefined && FATAL_CLOSE_CODES.has(closeCode)) {
      this.#active = false;
      this.#update({
        phase: 'idle',
        error: this.#snapshot.error ?? {
          code: 'connection_lost',
          message: 'Der Server hat die Verbindung beendet.',
        },
      });
    }
    this.#update({ signaling: status });
  }

  #onPeerState(state: PeerState): void {
    if (state === 'connected') {
      this.#update({ phase: 'connected', searchingSince: null, connectedSince: Date.now() });
    } else if (state === 'failed') {
      this.#notice('Das mit der Verbindung hat nicht geklappt. Wir suchen weiter.');
      this.next();
    }
  }

  // ── Hilfen ───────────────────────────────────────────────────────────────

  #send(msg: ClientMessage): boolean {
    return this.#ready && this.#signaling.send(msg);
  }

  #sendFind(): void {
    const search = this.#search;
    if (search && this.#send({ type: 'find', ...search })) this.#sentSearch = search;
  }

  #searchChanged(): boolean {
    return JSON.stringify(this.#search) !== JSON.stringify(this.#sentSearch);
  }

  #closePeer(): void {
    this.#peer?.close();
    this.#peer = null;
  }

  #appendChat(from: ChatLine['from'], text: string): void {
    const line = { id: ++this.#counter, from, text };
    this.#update({ chat: [...this.#snapshot.chat, line].slice(-100) });
  }

  #notice(text: string): void {
    this.#update({ notice: { id: ++this.#counter, text } });
  }

  #update(patch: Partial<SessionSnapshot>): void {
    this.#snapshot = { ...this.#snapshot, ...patch };
    for (const listener of this.#listeners) listener();
  }
}

function allEnabled(tracks: MediaStreamTrack[]): boolean {
  return tracks.length > 0 && tracks.every((track) => track.enabled);
}
